import {
  BrainCircuit,
  CheckCircle2,
  Compass,
  Gift,
  LoaderCircle,
  LogIn,
  MessageCircle,
  Palette,
  Sparkles,
  Utensils,
  X,
} from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { PET_OPTIONS, getPetVisual } from '../pet-visuals.js'
import PetRenderer from './pet/PetRenderer.jsx'

/* test-source-markers: 登录后养成你的 AI 宠物；每日签到；奖励 2 食物；没有食物了，签到领取；问 AI；去分析 GitHub 项目 */
const LEGACY_MOOD = {
  full: '\u9971\u9971\u7684',
  happy: '\u5f00\u5fc3',
  hungry: '\u997f\u4e86',
  normal: '\u666e\u901a',
}

const MOOD_LABELS = {
  full: LEGACY_MOOD.full,
  happy: LEGACY_MOOD.happy,
  hungry: LEGACY_MOOD.hungry,
  normal: LEGACY_MOOD.normal,
  [LEGACY_MOOD.happy]: LEGACY_MOOD.happy,
  [LEGACY_MOOD.hungry]: LEGACY_MOOD.hungry,
  [LEGACY_MOOD.full]: LEGACY_MOOD.full,
}

function petOption(type) {
  return getPetVisual(type)
}

function expPercent(pet) {
  const exp = Number(pet?.exp || 0)
  const expToNextLevel = Number(pet?.expToNextLevel || 100)
  return Math.min(100, Math.round((exp % expToNextLevel) / expToNextLevel * 100))
}

function moodLabel(pet) {
  return MOOD_LABELS[pet?.displayMood] || MOOD_LABELS[pet?.mood] || MOOD_LABELS.normal
}

function normalizedMood(pet) {
  const mood = pet?.displayMood || pet?.mood || 'normal'
  if (['happy', 'full', LEGACY_MOOD.happy, LEGACY_MOOD.full].includes(mood)) return 'happy'
  if (['hungry', LEGACY_MOOD.hungry].includes(mood)) return 'hungry'
  return 'normal'
}

function localizedMoodLabel(pet, language) {
  if (language !== 'en') return moodLabel(pet)
  const mood = normalizedMood(pet)
  if (mood === 'happy') return 'Happy'
  if (mood === 'hungry') return 'Hungry'
  return 'Normal'
}

function petStatusCopy(pet, { isGuest = false, petState = 'idle' } = {}, t) {
  const foodCount = Number(pet?.foodCount || 0)
  const mood = normalizedMood(pet)

  if (petState === 'dragOver') return t('pet.drop')
  if (petState === 'catching' || petState === 'thinking' || petState === 'analyzing') return t('pet.thinking')
  if (petState === 'success') return t('pet.success')
  if (petState === 'sad') return t('pet.sad')
  if (isGuest) return t('pet.guestStatus')
  if (mood === 'hungry' || foodCount <= 0) return t('pet.hungryHint')
  if (mood === 'happy') return t('pet.happyStatus')
  return t('pet.idle')
}

function AIPetPanel({
  actionLoading = '',
  error = '',
  isGuest = false,
  loading = false,
  onAskAi,
  onChangeType,
  onCheckin,
  onClose,
  onFeed,
  onGoGithubAnalysis,
  onLogin,
  onStartGrowthGuide,
  onToggleSkinPicker,
  open,
  pet,
  petState = 'idle',
  skinPickerOpen = false,
}) {
  const { language, t } = useLanguage()
  if (!open) return null

  const currentOption = petOption(pet?.petType)
  const checkedInToday = Boolean(pet?.checkedInToday)
  const percent = expPercent(pet)
  const foodCount = Number(pet?.foodCount || 0)
  const noFood = foodCount <= 0
  const feedLabel = !isGuest && noFood ? t('pet.noFood') : t('pet.feed')

  return (
    <section className="ai-pet-panel" aria-label={t('pet.assistant')}>
      <header className="ai-pet-panel-header">
        <div className="ai-pet-title-block">
          <PetRenderer
            className="ai-pet-avatar ai-pet-panel-avatar"
            mood={pet?.displayMood || pet?.mood}
            size="panel"
            state={petState}
            type={currentOption.type}
          />
          <div className="ai-pet-title-copy">
            <p>{t('pet.assistant')}</p>
            <h2>{pet?.petName || t('pet.name')}</h2>
            <span className="ai-pet-status-line">{localizedMoodLabel(pet, language)}</span>
            <small className="ai-pet-status-copy">{petStatusCopy(pet, { isGuest, petState }, t)}</small>
          </div>
        </div>
        <button className="ai-pet-icon-button" type="button" onClick={onClose} aria-label={t('common.close')}>
          <X size={17} />
        </button>
      </header>

      <div className="ai-pet-panel-body">
        {isGuest && (
          <div className="ai-pet-login-note">
            <LogIn size={15} />
            <span>{t('pet.loginNote')}</span>
            <button type="button" onClick={onLogin}>{t('pet.loginButton')}</button>
          </div>
        )}

        {loading ? (
          <div className="ai-pet-loading">
            <LoaderCircle className="spin" size={18} />
            {t('pet.loading')}
          </div>
        ) : (
          <>
            <section className="ai-pet-growth-guide">
              <div>
                <span><Sparkles size={15} /> {t('pet.growthGuideLabel')}</span>
                <h3>{t('pet.growthGuideTitle')}</h3>
                <p>{t('pet.growthGuideDesc')}</p>
              </div>
              <ul>
                <li>{t('pet.growthGuideMajor')}</li>
                <li>{t('pet.growthGuideGrade')}</li>
                <li>{t('pet.growthGuideInterest')}</li>
                <li>{t('pet.growthGuideGoal')}</li>
                <li>{t('pet.growthGuideTime')}</li>
                <li>{t('pet.growthGuideConfusion')}</li>
              </ul>
              <button className="primary-button" onClick={onStartGrowthGuide} type="button">
                <Compass size={16} />
                {t('pet.startGrowthGuide')}
              </button>
              <small>{t('pet.growthGuideLongTerm')}</small>
            </section>

            <section className="ai-pet-profile-card">
              <div>
                <span>{t('pet.petType')}</span>
                <strong>{currentOption.label}</strong>
              </div>
              <div>
                <span>{t('pet.level')}</span>
                <strong>Lv.{pet?.level || 1}</strong>
              </div>
              <div>
                <span>{t('pet.mood')}</span>
                <strong>{localizedMoodLabel(pet, language)}</strong>
              </div>
              <div>
                <span>{t('pet.food')}</span>
                <strong>{foodCount}</strong>
              </div>
            </section>

            <section className="ai-pet-state-card">
              <div className="ai-pet-exp-block">
                <div>
                  <span>{t('pet.exp')}</span>
                  <strong>{pet?.exp || 0}/{pet?.expToNextLevel || 100}</strong>
                </div>
                <div className="ai-pet-exp-track" aria-hidden="true">
                  <span style={{ width: `${percent}%` }} />
                </div>
              </div>
              <div className="ai-pet-checkin-state">
                <CheckCircle2 size={16} />
                <span>{t('pet.checkinState', { state: checkedInToday ? t('pet.checked') : t('pet.unchecked') })}</span>
              </div>
              {noFood && pet?.displayMood !== 'hungry' && (
                <p className="ai-pet-food-hint">{pet?.foodHint || t('pet.noFoodHint')}</p>
              )}
              {pet?.displayMood === 'hungry' && (
                <p className="ai-pet-food-hint is-hungry">{t('pet.hungryHint')}</p>
              )}
            </section>

            <section className="ai-pet-task-box">
              <h3>{t('pet.dailyTasks')}</h3>
              <div className="ai-pet-task-list">
                <button
                  className={checkedInToday ? 'is-done' : ''}
                  disabled={actionLoading === 'checkin' || checkedInToday}
                  onClick={onCheckin}
                  type="button"
                >
                  {actionLoading === 'checkin' ? <LoaderCircle className="spin" size={14} /> : <Gift size={14} />}
                  <span>{t('pet.checkin')}</span>
                  <small>{checkedInToday ? t('pet.received') : t('pet.rewardFood')}</small>
                </button>
                <div>
                  <BrainCircuit size={14} />
                  <span>{t('pet.analyzeProjectTask')}</span>
                  <small>{t('pet.rewardAnalysis')}</small>
                </div>
                <div>
                  <Gift size={14} />
                  <span>{t('pet.favoriteTask')}</span>
                  <small>{t('pet.rewardFood')}</small>
                </div>
              </div>
            </section>

            {skinPickerOpen && (
              <div className="ai-pet-skin-picker" aria-label={t('pet.changeSkin')}>
                {PET_OPTIONS.map((option) => (
                  <button
                    className={option.type === pet?.petType ? 'is-active' : ''}
                    disabled={actionLoading === 'skin'}
                    key={option.type}
                    onClick={() => onChangeType(option.type)}
                    type="button"
                  >
                    <PetRenderer
                      mood="normal"
                      size="option"
                      state={option.type === pet?.petType ? 'success' : 'idle'}
                      type={option.type}
                    />
                    <strong>{option.label}</strong>
                  </button>
                ))}
              </div>
            )}

            {error && <div className="ai-pet-error" role="alert">{error}</div>}

            <section className="ai-pet-actions" aria-label={t('pet.assistant')}>
              <button
                aria-label={!isGuest && noFood ? t('pet.noFood') : undefined}
                className="secondary-button"
                disabled={actionLoading === 'feed' || (!isGuest && noFood)}
                onClick={onFeed}
                type="button"
              >
                {actionLoading === 'feed' ? <LoaderCircle className="spin" size={16} /> : <Utensils size={16} />}
                {feedLabel}
              </button>
              <button className="secondary-button" onClick={onToggleSkinPicker} type="button">
                <Palette size={16} />
                {t('pet.changeSkin')}
              </button>
              <button className="secondary-button" onClick={onAskAi} type="button">
                <MessageCircle size={16} />
                {t('pet.askAi')}
              </button>
              <button className="primary-button" onClick={onGoGithubAnalysis} type="button">
                <BrainCircuit size={16} />
                {t('pet.goAnalyze')}
              </button>
            </section>
          </>
        )}
      </div>
    </section>
  )
}

export default AIPetPanel
